import { createContext, useContext, useEffect, useState } from "react";
import apiClient from "../api/apiClient";

const JWTContext = createContext(null);

export function JWTProvider({ children }) {
  const [token, setToken] = useState(null);
  const [userId, setUserId] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // 1) 최초 로딩 시 localStorage → Context 동기화
  useEffect(() => {
    const storedToken = localStorage.getItem("accessToken");
    const storedUserId = localStorage.getItem("userId");

    if (storedToken && storedUserId) {
      setToken(storedToken);
      setUserId(storedUserId);
    }
  }, []);

  // JWT에서 sub(userId) 추출
  const extractUserId = (jwt) => {
    try {
      const payload = JSON.parse(atob(jwt.split(".")[1]));
      return payload.sub;
    } catch (e) {
      console.error("JWT 파싱 실패", e);
      return null;
    }
  };

  // 2) userId 변경될 때 자동 조회
  useEffect(() => {
    if (!userId || !token) return;

    const fetchUser = async () => {
      if (isLoading) return;
      setIsLoading(true);

      try {
        const res = await apiClient.get(`/user/${userId}`);
        setUser(res.data);
      } catch (err) {
        const status = err?.response?.status;

        if (status && [400, 401, 403, 404].includes(status)) {
          // 👉 잘못된 / 오래된 로그인 정보라고 보고 조용히 초기화
          localStorage.removeItem("accessToken");
          localStorage.removeItem("userId");

          setToken(null);
          setUserId(null);
          setUser(null);
          // 굳이 console.error 안 찍음 (거슬리는 빨간 로그 제거)
        } else {
          // 진짜 서버 장애(500 등)만 로그 찍기
          console.error("유저 조회 실패:", err);
          setUser(null);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [userId, token]);

  // 3) 로그인 시 사용
  const login = async (newToken) => {
    localStorage.setItem("accessToken", newToken);

    const uid = extractUserId(newToken);

    // 저장
    localStorage.setItem("userId", uid);

    // 상태 반영
    setToken(newToken);
    setUserId(uid);

    // 첫 유저 조회
    try {
      const res = await apiClient.get(`/user/${uid}`);
      setUser(res.data);
    } catch (err) {
      console.error("로그인 후 유저조회 실패:", err);
    }
  };

  // 4) 로그아웃
  const logout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userId");

    setToken(null);
    setUserId(null);
    setUser(null);
  };

  return (
    <JWTContext.Provider
      value={{ token, userId, user, setUser, isLoading, login, logout }}
    >
      {children}
    </JWTContext.Provider>
  );
}

export function useJWT() {
  return useContext(JWTContext);
}
